import {
  decryptAesGcm,
  decodeBase64,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
  unpackEncryptedBlob,
} from './primitives';
import { unwrapKey } from './wrappedKey';

export const SHARE_LABEL_BLOB_AAD = 'shareweb:share-label:v1';
export const INFO_SHARE_LABEL_HKDF_INFO = 'shareweb:info-share-label:v1';
export const MAX_SHARE_LABEL_LENGTH = 200;

const folderCreateWrapContext = 'shareweb:folder:create';

function folderMemberWrapContext(folderId: string): string {
  return `shareweb:folder:member:${folderId}`;
}

export async function deriveInfoShareLabelKey(
  userPrivateKey: Uint8Array,
  shareId: string,
): Promise<CryptoKey> {
  const material = await hkdfSha256(
    userPrivateKey,
    new TextEncoder().encode(shareId),
    INFO_SHARE_LABEL_HKDF_INFO,
    32,
  );
  return importAesKey(material);
}

export async function encryptShareLabel(
  key: CryptoKey,
  label: string,
): Promise<Uint8Array> {
  const trimmed = label.trim();
  if (trimmed.length === 0) {
    throw new Error('empty_label');
  }
  if (trimmed.length > MAX_SHARE_LABEL_LENGTH) {
    throw new Error('label_too_long');
  }

  const encrypted = await encryptAesGcm(
    key,
    new TextEncoder().encode(trimmed),
    new TextEncoder().encode(SHARE_LABEL_BLOB_AAD),
  );
  return packEncryptedBlob(encrypted);
}

export async function decryptShareLabel(key: CryptoKey, blob: Uint8Array): Promise<string> {
  const decrypted = await decryptAesGcm(
    key,
    unpackEncryptedBlob(blob),
    new TextEncoder().encode(SHARE_LABEL_BLOB_AAD),
  );
  return new TextDecoder().decode(decrypted);
}

export async function encryptInfoShareLabel(
  userPrivateKey: Uint8Array,
  shareId: string,
  label: string,
): Promise<Uint8Array> {
  const key = await deriveInfoShareLabelKey(userPrivateKey, shareId);
  return encryptShareLabel(key, label);
}

export async function decryptInfoShareLabel(
  userPrivateKey: Uint8Array,
  shareId: string,
  blob: Uint8Array,
): Promise<string> {
  const key = await deriveInfoShareLabelKey(userPrivateKey, shareId);
  return decryptShareLabel(key, blob);
}

export async function encryptFolderShareLabel(
  folderKey: Uint8Array,
  label: string,
): Promise<Uint8Array> {
  const key = await importAesKey(folderKey);
  return encryptShareLabel(key, label);
}

export async function decryptFolderShareLabel(
  folderKey: Uint8Array,
  blob: Uint8Array,
): Promise<string> {
  const key = await importAesKey(folderKey);
  return decryptShareLabel(key, blob);
}

export async function unwrapFolderKeyForMember(
  wrappedFolderKeyBase64: string,
  folderId: string,
  userPrivateKey: Uint8Array,
): Promise<Uint8Array> {
  const wrapped = decodeBase64(wrappedFolderKeyBase64);
  const contexts = [folderCreateWrapContext, folderMemberWrapContext(folderId)];
  let lastError: unknown;
  for (const context of contexts) {
    try {
      return await unwrapKey(wrapped, userPrivateKey, context);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error('folder_key_unwrap_failed');
}

export function folderMemberWrapContextForInvite(folderId: string): string {
  return folderMemberWrapContext(folderId);
}
